import { useState } from 'react';
import { QuoteCompaisonColumnDatabase } from '../../../../types';
import { useDeleteQuoteColumn } from '../../api/useDeleteQuoteColumn';
import { useReorderQuoteColumns } from '../../api/useReorderQuoteColumns';
import { useUpdateQuoteColumnVisibility } from '../../api/useUpdateQuoteColumnVisibility';

type Props = {
  groupId: string;
  columns: QuoteCompaisonColumnDatabase[];
};

/** Moves the item at `index` by `offset` places. */
export function moveItem<Item>(items: Item[], index: number, offset: number) {
  const target = index + offset;

  if (target < 0 || target >= items.length) {
    return items;
  }

  const reordered = [...items];
  const [moved] = reordered.splice(index, 1);
  reordered.splice(target, 0, moved);

  return reordered;
}

export function useQuoteColumnsDialog(props: Props) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [columnToEdit, setColumnToEdit] = useState<QuoteCompaisonColumnDatabase | null>(null);
  const [columnToDelete, setColumnToDelete] = useState<QuoteCompaisonColumnDatabase | null>(null);
  const { mutate: updateVisibility } = useUpdateQuoteColumnVisibility();
  const { mutate: reorder, isPending: isReordering } = useReorderQuoteColumns();
  const { mutateAsync: deleteColumn } = useDeleteQuoteColumn();

  const nextOrder = Math.max(-1, ...props.columns.map((column) => column.order)) + 1;

  function handleToggleVisibility(column: QuoteCompaisonColumnDatabase) {
    updateVisibility({ groupId: props.groupId, columnId: column.id, visible: !column.visible });
  }

  function handleMove(index: number, offset: number) {
    const columnIds = moveItem(props.columns, index, offset).map((column) => column.id);
    reorder({ groupId: props.groupId, columnIds });
  }

  function handleConfirmDelete() {
    return deleteColumn({ groupId: props.groupId, columnId: columnToDelete!.id });
  }

  return {
    nextOrder,
    isReordering,
    isCreateOpen,
    columnToEdit,
    columnToDelete,
    handleToggleVisibility,
    handleMove,
    handleConfirmDelete,
    handleOpenCreate: () => setIsCreateOpen(true),
    handleCloseCreate: () => setIsCreateOpen(false),
    handleOpenEdit: setColumnToEdit,
    handleCloseEdit: () => setColumnToEdit(null),
    handleOpenDelete: setColumnToDelete,
    handleCloseDelete: () => setColumnToDelete(null),
  };
}
